import type { stacks } from "@dropbox/api-v2-client";
import type { ButtonProps } from "@dropbox/dig-components/buttons";
import { UIIcon } from "@dropbox/dig-icons";
import { DropboxDashLine } from "@dropbox/dig-icons/assets";
import { PAP_Click_StackChat } from "@mirage/analytics/events/types/click_stack_chat";
import { createUxaElementId } from "@mirage/analytics/uxa";
import { ButtonWithTooltip } from "@mirage/shared/buttons/ButtonWithTooltip";
import { useReportStackPapEvent } from "@mirage/stacks/hooks/useReportStackPapEvent";
import i18n from "@mirage/translations";
import { useAtomValue } from "jotai";
import { useCallback } from "react";
import { stackChatSidebarOpenAtom } from "../atoms/chatSidebarAtoms";
import { useStackChatSources } from "../hooks/FullScreenStackHooks";
import { useLoadStackAskDashChat } from "../hooks/useStackChat";

type StackAskDashButtonProps = Partial<ButtonProps> & {
  stack: stacks.Stack | null;
};

export const StackAskDashButton = ({
  stack,
  ...props
}: StackAskDashButtonProps) => {
  const isStackChatSidebarOpen = useAtomValue(stackChatSidebarOpenAtom);
  const { hasChatSources } = useStackChatSources();
  const reportStackPapEvent = useReportStackPapEvent();
  const loadStackAskDashChat = useLoadStackAskDashChat();
  const isNewStack = !stack?.namespace_id;

  const onClick = useCallback(() => {
    reportStackPapEvent(
      PAP_Click_StackChat({
        actionSurfaceComponent: "stack_header",
      }),
    );
    loadStackAskDashChat();
  }, [reportStackPapEvent, loadStackAskDashChat]);

  // chat sidebar is already open, so don't show this button
  if (isStackChatSidebarOpen && hasChatSources) {
    return null;
  }
  return (
    <ButtonWithTooltip
      aria-label={i18n.t("ask_dash")}
      data-testid="StackAskDash"
      data-uxa-entity-id={stack?.namespace_id ?? ""}
      data-uxa-log={createUxaElementId("ask_dash_button", {
        actionSurfaceComponent: "stacks",
        featureLine: "stacks",
      })}
      disabled={isNewStack || !hasChatSources}
      onClick={onClick}
      variant="opacity"
      size="large"
      tone="neutral"
      tooltipProps={{
        title: !hasChatSources
          ? i18n.t("no_documents_to_summarize")
          : undefined,
      }}
      withIconStart={<UIIcon src={DropboxDashLine} />}
      {...props}
    >
      {i18n.t("dash")}
    </ButtonWithTooltip>
  );
};
